import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCalendarMonth,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  CalendarMonthData,
  CalendarEvent,
  SubscriptionRenewalEvent,
  CalendarEventCategory,
  CreateCalendarEventRequest,
  getClients,
} from '../../services/api';
import { Client } from '../../types';

// Category colors matching autotask-hub style
const CATEGORY_COLORS: Record<CalendarEventCategory | 'renewal', { bg: string; hex: string }> = {
  renewal: { bg: 'bg-blue-500', hex: '#3b82f6' },
  reminder: { bg: 'bg-amber-500', hex: '#f59e0b' },
  'action-required': { bg: 'bg-red-500', hex: '#ef4444' },
  'follow-up': { bg: 'bg-purple-500', hex: '#a855f7' },
  migration: { bg: 'bg-emerald-500', hex: '#10b981' },
};

const CATEGORY_LABELS: Record<CalendarEventCategory | 'renewal', string> = {
  renewal: 'Renewals',
  reminder: 'Reminder',
  'action-required': 'Action Required',
  'follow-up': 'Follow-up',
  migration: 'Migration',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

const shortDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const MAX_VISIBLE_EVENTS = 3;

type CalendarItem = CalendarEvent | SubscriptionRenewalEvent;

function isRenewalEvent(item: CalendarItem): item is SubscriptionRenewalEvent {
  return 'type' in item && item.type === 'renewal';
}

function getItemCategory(item: CalendarItem): CalendarEventCategory | 'renewal' {
  if (isRenewalEvent(item)) return 'renewal';
  return item.category;
}

function getItemTitle(item: CalendarItem): string {
  if (isRenewalEvent(item)) {
    return `${item.productName} (${item.quantity})`;
  }
  return item.title;
}

function getItemClient(item: CalendarItem): string {
  if (isRenewalEvent(item)) return item.clientName;
  return item.clientName || '';
}

export default function RenewalCalendar() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthData, setMonthData] = useState<CalendarMonthData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mini calendar state (can navigate independently)
  const [miniCalendarDate, setMiniCalendarDate] = useState(currentDate);

  // Sync mini calendar with main calendar when main changes
  useEffect(() => {
    setMiniCalendarDate(currentDate);
  }, [currentDate]);

  // Category filter - start with all selected
  const [selectedCategories, setSelectedCategories] = useState<Set<CalendarEventCategory | 'renewal'>>(
    new Set(['renewal', 'reminder', 'action-required', 'follow-up', 'migration'])
  );

  // Event popup state
  const [viewingItem, setViewingItem] = useState<CalendarItem | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null);

  // Overflow popup state
  const [overflowPopup, setOverflowPopup] = useState<{ day: number; items: CalendarItem[]; x: number; y: number } | null>(null);

  // Add/Edit event modal
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [modalDate, setModalDate] = useState<Date | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Form fields
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventCategory, setEventCategory] = useState<CalendarEventCategory>('reminder');
  const [eventDate, setEventDate] = useState('');

  // Client selection
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');

  // Load clients
  useEffect(() => {
    async function loadClients() {
      try {
        const data = await getClients({ pageSize: 500 });
        setClients(data.items.sort((a, b) => a.companyName.localeCompare(b.companyName)));
      } catch (err) {
        console.error('Failed to load clients:', err);
      }
    }
    loadClients();
  }, []);

  // Load calendar data
  const loadCalendarData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const data = await getCalendarMonth(year, month);
      setMonthData(data);
    } catch (err) {
      console.error('Failed to load calendar data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load calendar data');
      setMonthData(null);
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => { loadCalendarData(); }, [loadCalendarData]);

  // Navigation
  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const goToToday = () => setCurrentDate(new Date());

  const miniCalendarPrevMonth = () => setMiniCalendarDate(new Date(miniCalendarDate.getFullYear(), miniCalendarDate.getMonth() - 1, 1));
  const miniCalendarNextMonth = () => setMiniCalendarDate(new Date(miniCalendarDate.getFullYear(), miniCalendarDate.getMonth() + 1, 1));

  // Category toggle
  const toggleCategory = (cat: CalendarEventCategory | 'renewal') => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedCategories(new Set(['renewal', 'reminder', 'action-required', 'follow-up', 'migration']));
  };

  const selectNone = () => {
    setSelectedCategories(new Set());
  };

  // Get items for a day
  const getItemsForDay = useCallback((day: number): CalendarItem[] => {
    if (!monthData) return [];
    const key = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const items = monthData.byDate[key] || [];
    if (selectedCategories.size === 0) return [];
    return items.filter(item => selectedCategories.has(getItemCategory(item)));
  }, [monthData, currentDate, selectedCategories]);

  // Calendar grid calculations
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const isCurrentMonth = month === today.getMonth() && year === today.getFullYear();
  const isToday = (day: number) => isCurrentMonth && day === today.getDate();

  // Build days array for main calendar
  const getDaysInMonth = () => {
    const days: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    while (days.length % 7 !== 0) days.push(null);
    return days;
  };

  // Mini calendar helpers - shows full 6 rows with prev/next month days
  const getMiniCalendarDays = () => {
    const miniYear = miniCalendarDate.getFullYear();
    const miniMonth = miniCalendarDate.getMonth();
    const miniFirstDay = new Date(miniYear, miniMonth, 1).getDay();
    const miniDaysInMonth = new Date(miniYear, miniMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(miniYear, miniMonth, 0).getDate();

    const days: { day: number; currentMonth: boolean }[] = [];

    // Previous month days
    for (let i = miniFirstDay - 1; i >= 0; i--) {
      days.push({ day: daysInPrevMonth - i, currentMonth: false });
    }

    // Current month days
    for (let d = 1; d <= miniDaysInMonth; d++) {
      days.push({ day: d, currentMonth: true });
    }

    // Next month days - fill to 6 rows (42 days)
    let nextDay = 1;
    while (days.length < 42) {
      days.push({ day: nextDay++, currentMonth: false });
    }

    return days;
  };

  const days = getDaysInMonth();
  const miniDays = getMiniCalendarDays();
  const isMiniCurrentMonth = miniCalendarDate.getMonth() === today.getMonth() && miniCalendarDate.getFullYear() === today.getFullYear();

  // Categories with counts this month
  const categoriesWithCounts = (() => {
    const result: { category: CalendarEventCategory | 'renewal'; count: number; value?: number }[] = [];

    const renewalCount = monthData?.renewals?.length || 0;
    const renewalValue = monthData?.renewals?.reduce((sum, r) => sum + r.totalPrice, 0) || 0;
    result.push({ category: 'renewal', count: renewalCount, value: renewalValue });

    const cats: CalendarEventCategory[] = ['reminder', 'action-required', 'follow-up', 'migration'];
    for (const cat of cats) {
      const count = monthData?.events?.filter(e => e.category === cat).length || 0;
      result.push({ category: cat, count });
    }

    return result;
  })();

  // Filtered stats
  const filteredStats = {
    renewalCount: selectedCategories.has('renewal') ? (monthData?.renewals?.length || 0) : 0,
    eventCount: monthData?.events?.filter(e => selectedCategories.has(e.category)).length || 0,
    totalValue: selectedCategories.has('renewal')
      ? (monthData?.renewals?.reduce((sum, r) => sum + r.totalPrice, 0) || 0)
      : 0,
  };

  // Event popup handlers
  const openItemPopup = (item: CalendarItem, e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const x = overflowPopup ? overflowPopup.x + 400 : rect.right + 8;
    const y = rect.top;
    setPopupPosition({
      x: Math.min(x, window.innerWidth - 320),
      y: Math.min(Math.max(y, 10), window.innerHeight - 280)
    });
    setViewingItem(item);
  };

  const openOverflowPopup = (day: number, items: CalendarItem[], e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setOverflowPopup({
      day,
      items,
      x: Math.min(rect.right + 8, window.innerWidth - 400),
      y: Math.min(rect.top, window.innerHeight - 350)
    });
    setViewingItem(null);
  };

  // Close all popups
  const closeAllPopups = () => {
    setViewingItem(null);
    setPopupPosition(null);
    setOverflowPopup(null);
  };

  // Add event modal
  const openAddModal = (day: number) => {
    const date = new Date(year, month, day);
    setModalDate(date);
    setEventDate(`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
    setEventTitle('');
    setEventDescription('');
    setEventCategory('reminder');
    setSelectedClientId('');
    setEditingEvent(null);
    setFormError(null);
    setShowEventModal(true);
  };

  const openEditModal = (event: CalendarEvent) => {
    setEditingEvent(event);
    setEventTitle(event.title);
    setEventDescription(event.description);
    setEventCategory(event.category);
    setEventDate(event.date);
    setSelectedClientId(event.clientId || '');
    setModalDate(new Date(event.date));
    setFormError(null);
    setShowEventModal(true);
    setViewingItem(null);
    setPopupPosition(null);
  };

  const handleMiniCalendarDayClick = (day: number, currentMonth: boolean) => {
    if (currentMonth) {
      // Navigate main calendar to this month
      setCurrentDate(new Date(miniCalendarDate.getFullYear(), miniCalendarDate.getMonth(), 1));
      openAddModal(day);
    } else {
      // Clicked on prev/next month day
      const dayIndex = miniDays.findIndex(d => d.day === day && !d.currentMonth);
      if (dayIndex < 7) {
        // Previous month
        const newDate = new Date(miniCalendarDate.getFullYear(), miniCalendarDate.getMonth() - 1, day);
        setCurrentDate(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
        setMiniCalendarDate(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
      } else {
        // Next month
        const newDate = new Date(miniCalendarDate.getFullYear(), miniCalendarDate.getMonth() + 1, day);
        setCurrentDate(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
        setMiniCalendarDate(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
      }
    }
  };

  const handleSubmitEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventTitle.trim() || !eventDate) {
      setFormError('Title and date are required');
      return;
    }

    setFormSubmitting(true);
    setFormError(null);

    try {
      const selectedClient = clients.find(c => c.id === selectedClientId);

      if (editingEvent) {
        await updateCalendarEvent(editingEvent.id, {
          title: eventTitle.trim(),
          description: eventDescription.trim(),
          date: eventDate,
          category: eventCategory,
        });
      } else {
        const request: CreateCalendarEventRequest = {
          title: eventTitle.trim(),
          description: eventDescription.trim(),
          date: eventDate,
          category: eventCategory,
          clientId: selectedClientId || undefined,
          clientName: selectedClient?.companyName,
        };
        await createCalendarEvent(request);
      }

      setShowEventModal(false);
      loadCalendarData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save event');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!editingEvent) return;
    if (!confirm('Are you sure you want to delete this event?')) return;

    setFormSubmitting(true);
    try {
      await deleteCalendarEvent(editingEvent.id);
      setShowEventModal(false);
      loadCalendarData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to delete event');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Navigate to client
  const navigateToClient = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  return (
    <div className="flex h-full w-full gap-3 overflow-hidden">
      {/* Shared backdrop for popups */}
      {(viewingItem || overflowPopup) && (
        <div className="fixed inset-0 z-40" onClick={closeAllPopups} />
      )}

      {/* LEFT SIDEBAR - matching autotask-hub layout */}
      <div className="w-56 flex-shrink-0 flex flex-col bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        {/* Mini Calendar */}
        <div className="p-3 border-b border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-white">
              {monthNames[miniCalendarDate.getMonth()]} {miniCalendarDate.getFullYear()}
            </span>
            <div className="flex items-center">
              <button onClick={miniCalendarPrevMonth} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button onClick={miniCalendarNextMonth} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Mini calendar grid */}
          <div className="grid grid-cols-7 gap-0 text-center">
            {shortDays.map((d, i) => (
              <div key={i} className="text-[10px] text-slate-500 py-1">{d}</div>
            ))}
            {miniDays.map((item, idx) => {
              const isMiniToday = isMiniCurrentMonth && item.currentMonth && item.day === today.getDate();
              const dateKey = item.currentMonth
                ? `${miniCalendarDate.getFullYear()}-${String(miniCalendarDate.getMonth() + 1).padStart(2, '0')}-${String(item.day).padStart(2, '0')}`
                : null;
              const hasEntries = dateKey && monthData?.byDate?.[dateKey]?.length;
              return (
                <button
                  key={idx}
                  onClick={() => handleMiniCalendarDayClick(item.day, item.currentMonth)}
                  className={`text-xs p-0.5 rounded-full w-6 h-6 flex items-center justify-center mx-auto transition-colors
                    ${item.currentMonth ? 'hover:bg-slate-700' : 'hover:bg-slate-700/50'}
                    ${isMiniToday ? 'bg-blue-600 text-white font-medium' : item.currentMonth ? 'text-slate-300' : 'text-slate-600'}
                    ${hasEntries && !isMiniToday ? 'font-bold' : ''}
                  `}
                >
                  {item.day}
                </button>
              );
            })}
          </div>
        </div>

        {/* Category filters - scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Categories</span>
            </div>
            <div className="flex gap-3 mb-3 text-xs">
              <button onClick={selectAll} className="text-blue-400 hover:text-blue-300">All</button>
              <button onClick={selectNone} className="text-blue-400 hover:text-blue-300">None</button>
            </div>
            <div className="space-y-1">
              {categoriesWithCounts.map(({ category, count, value }) => {
                const color = CATEGORY_COLORS[category];
                const isChecked = selectedCategories.has(category);
                return (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    className="flex items-center gap-2 w-full py-1 px-1 rounded hover:bg-slate-700/50 transition-colors text-left"
                  >
                    <span
                      className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center transition-colors ${
                        isChecked ? color.bg : 'border-2 border-slate-500'
                      }`}
                    >
                      {isChecked && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </span>
                    <span className={`text-xs flex-1 truncate ${isChecked ? 'text-slate-200' : 'text-slate-500'}`}>
                      {CATEGORY_LABELS[category]}
                    </span>
                    <span className={`text-xs ${isChecked ? 'text-slate-400' : 'text-slate-600'}`}>
                      {count}
                      {value !== undefined && value > 0 && (
                        <span className="ml-1 text-emerald-400">{formatCurrency(value)}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer with stats */}
        <div className="p-3 border-t border-slate-700 bg-slate-800/50">
          <div className="flex justify-between text-xs">
            <span className="text-slate-400">Renewals</span>
            <span className="text-white font-medium">{filteredStats.renewalCount}</span>
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="text-slate-400">Events</span>
            <span className="text-slate-300">{filteredStats.eventCount}</span>
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="text-slate-400">Value</span>
            <span className="text-emerald-400 font-medium">{formatCurrency(filteredStats.totalValue)}</span>
          </div>
        </div>
      </div>

      {/* MAIN CALENDAR */}
      <div className="flex-1 flex flex-col min-w-0 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-xl">
        {/* Header with navigation */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Today
            </button>
            <div className="flex items-center">
              <button onClick={prevMonth} className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-lg font-medium text-white min-w-[180px] text-center">
                {monthNames[month]} {year}
              </span>
              <button onClick={nextMonth} className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          <button
            onClick={() => openAddModal(today.getDate())}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Event
          </button>
        </div>

        {error && (
          <div className="mx-3 mt-2 p-2 bg-red-900/30 border border-red-600/50 rounded-lg text-red-400 text-xs">
            {error}
            <button onClick={loadCalendarData} className="ml-2 text-blue-400 hover:text-blue-300">Retry</button>
          </div>
        )}

        {/* Calendar Grid */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-slate-700">
            {dayNames.map(d => (
              <div key={d} className="py-2 text-center text-xs font-medium text-slate-400 border-r border-slate-700/50 last:border-r-0">
                {d}
              </div>
            ))}
          </div>

          {/* Days grid */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex items-center gap-2 text-slate-400">
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading...
              </div>
            </div>
          ) : (
            <div className="flex-1 grid grid-cols-7 auto-rows-fr min-h-0">
              {days.map((day, idx) => {
                const dayItems = day ? getItemsForDay(day) : [];
                const visible = dayItems.slice(0, MAX_VISIBLE_EVENTS);
                const overflow = dayItems.length - MAX_VISIBLE_EVENTS;
                const isLastRow = idx >= days.length - 7;

                return (
                  <div
                    key={idx}
                    onClick={() => day && openAddModal(day)}
                    className={`border-r border-b border-slate-700/50 flex flex-col cursor-pointer transition-colors relative
                      ${!day ? 'bg-slate-900/20' : 'bg-slate-900/40 hover:bg-slate-800/60'}
                      ${idx % 7 === 6 ? 'border-r-0' : ''}
                      ${isLastRow ? 'border-b-0' : ''}
                    `}
                  >
                    {day && (
                      <>
                        {/* Day header */}
                        <div className="flex items-center justify-between px-1.5 py-1">
                          <span className="text-[10px] text-slate-500">
                            {dayItems.length > 0 && `#${dayItems.length}`}
                          </span>
                          <span className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full
                            ${isToday(day) ? 'bg-blue-600 text-white' : 'text-slate-400'}`}
                          >
                            {day}
                          </span>
                          <span className="w-4" />
                        </div>

                        {/* Events */}
                        <div className="flex-1 px-1 pb-1 space-y-0.5 overflow-hidden">
                          {visible.map((item) => {
                            const color = CATEGORY_COLORS[getItemCategory(item)];
                            return (
                              <button
                                key={item.id}
                                onClick={(e) => { e.stopPropagation(); openItemPopup(item, e); }}
                                className={`w-full text-left px-1.5 py-0.5 rounded text-[11px] truncate ${color.bg} text-white hover:opacity-90 transition-opacity`}
                                title={`${getItemClient(item)}: ${getItemTitle(item)}`}
                              >
                                {getItemTitle(item)}
                              </button>
                            );
                          })}
                          {overflow > 0 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openOverflowPopup(day, dayItems, e); }}
                              className="w-full text-left px-1.5 py-0.5 text-[11px] text-blue-400 hover:text-blue-300 hover:bg-slate-700/50 rounded transition-colors"
                            >
                              +{overflow} more
                            </button>
                          )}
                        </div>

                        {/* Plus button */}
                        <div className="px-1 pb-1 flex justify-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); openAddModal(day); }}
                            className="w-5 h-5 rounded-full flex items-center justify-center text-slate-500 hover:text-blue-400 hover:bg-slate-700/50 transition-colors"
                            title="Add event"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* OVERFLOW POPUP */}
      {overflowPopup && (
        <div
          className="fixed bg-slate-800 rounded-xl shadow-2xl border border-slate-600 w-96 max-h-80 overflow-hidden z-50"
          style={{ left: overflowPopup.x, top: overflowPopup.y }}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-3 border-b border-slate-700">
            <span className="text-sm font-medium text-white">
              {monthNames[month]} {overflowPopup.day} - {overflowPopup.items.length} events
            </span>
            <button onClick={() => setOverflowPopup(null)} className="p-1 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-2 overflow-y-auto max-h-60 space-y-1">
            {overflowPopup.items.map((item) => {
              const color = CATEGORY_COLORS[getItemCategory(item)];
              const isSelected = viewingItem?.id === item.id;
              return (
                <button
                  key={item.id}
                  onClick={(e) => openItemPopup(item, e)}
                  className={`w-full text-left px-3 py-2 rounded-lg ${color.bg} text-white hover:opacity-90 transition-opacity ${isSelected ? 'ring-2 ring-white' : ''}`}
                >
                  <div className="font-medium text-sm">{getItemTitle(item)}</div>
                  {getItemClient(item) && (
                    <div className="text-xs opacity-80">{getItemClient(item)}</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ITEM DETAIL POPUP */}
      {viewingItem && popupPosition && (
        <div
          className="fixed bg-slate-800 rounded-xl shadow-2xl border border-slate-600 w-80 z-[60]"
          style={{ left: popupPosition.x, top: popupPosition.y }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header with actions */}
          <div className="flex items-center justify-between p-2 border-b border-slate-700">
            <div className="flex items-center gap-1">
              {!isRenewalEvent(viewingItem) && (
                <button
                  onClick={() => openEditModal(viewingItem)}
                  className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white"
                  title="Edit"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
            </div>
            <button onClick={closeAllPopups} className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Color bar */}
          <div className={`h-1 ${CATEGORY_COLORS[getItemCategory(viewingItem)].bg}`} />

          {/* Content */}
          <div className="p-3">
            <div className="text-lg font-medium text-white">{getItemTitle(viewingItem)}</div>
            {getItemClient(viewingItem) && (
              <div className="text-sm text-slate-400 mb-2">{getItemClient(viewingItem)}</div>
            )}

            {isRenewalEvent(viewingItem) ? (
              <div className="space-y-2 text-sm mt-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Quantity:</span>
                  <span className="text-slate-300">{viewingItem.quantity} licenses</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Amount:</span>
                  <span className="text-slate-300">{formatCurrency(viewingItem.totalPrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Billing:</span>
                  <span className="text-slate-300 capitalize">{viewingItem.billingTerm}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Auto-renew:</span>
                  <span className={viewingItem.autoRenew ? 'text-emerald-400' : 'text-amber-400'}>
                    {viewingItem.autoRenew === null ? 'Unknown' : viewingItem.autoRenew ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Source:</span>
                  <span className="text-slate-300 capitalize">{viewingItem.source}</span>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-300 mt-2">{viewingItem.description || 'No description'}</div>
            )}

            {/* View Client button */}
            {(isRenewalEvent(viewingItem) ? viewingItem.clientId : viewingItem.clientId) && (
              <button
                onClick={() => navigateToClient(isRenewalEvent(viewingItem) ? viewingItem.clientId : viewingItem.clientId!)}
                className="w-full mt-4 px-3 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                View Client
              </button>
            )}
          </div>
        </div>
      )}

      {/* ADD/EDIT EVENT MODAL */}
      {showEventModal && modalDate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]" onClick={() => setShowEventModal(false)}>
          <div className="bg-slate-800 rounded-xl border border-slate-600 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <span className="text-base font-medium text-white">
                  {editingEvent ? 'Edit Event' : 'Add Event'}
                </span>
                <div className="flex items-center gap-1 text-slate-400">
                  <button
                    onClick={() => setModalDate(new Date(modalDate.getFullYear(), modalDate.getMonth(), modalDate.getDate() - 1))}
                    className="p-1 hover:bg-slate-700 rounded"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm min-w-[80px] text-center">
                    {modalDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <button
                    onClick={() => setModalDate(new Date(modalDate.getFullYear(), modalDate.getMonth(), modalDate.getDate() + 1))}
                    className="p-1 hover:bg-slate-700 rounded"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
              <button onClick={() => setShowEventModal(false)} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmitEvent} className="p-4 space-y-4">
              {formError && <div className="text-red-400 text-sm bg-red-900/30 rounded-lg p-3">{formError}</div>}

              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Title *</label>
                <input
                  type="text"
                  value={eventTitle}
                  onChange={e => setEventTitle(e.target.value)}
                  placeholder="Event title"
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Category *</label>
                <select
                  value={eventCategory}
                  onChange={e => setEventCategory(e.target.value as CalendarEventCategory)}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-blue-500 focus:outline-none"
                >
                  {(['reminder', 'action-required', 'follow-up', 'migration'] as CalendarEventCategory[]).map(cat => (
                    <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Client (optional)</label>
                <select
                  value={selectedClientId}
                  onChange={e => setSelectedClientId(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-blue-500 focus:outline-none"
                >
                  <option value="">No client linked</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.companyName}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Description</label>
                <textarea
                  value={eventDescription}
                  onChange={e => setEventDescription(e.target.value)}
                  placeholder="Event details..."
                  rows={3}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2.5 focus:border-blue-500 focus:outline-none resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                {editingEvent && (
                  <button
                    type="button"
                    onClick={handleDeleteEvent}
                    disabled={formSubmitting}
                    className="px-3 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                  >
                    Delete
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowEventModal(false)}
                  className="flex-1 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting || !eventTitle.trim()}
                  className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                >
                  {formSubmitting && (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  {formSubmitting ? 'Saving...' : editingEvent ? 'Save Changes' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
